import { DeepgramSTT } from "./deepgramStt";

// Browser STT (Chrome/Edge only) — kept as a fallback when the backend Deepgram
// proxy is unavailable or returns an error.

export interface STTOptions {
  lang: string;
  onResult: (text: string, isFinal: boolean) => void;
  onError?: (err: string) => void;
  onEnd?: () => void;
  // Only honored when the Deepgram backend is active. Browser SR can't capture
  // raw audio. When true, callers can read the audio via getAudioBlob() after
  // a final transcript fires.
  captureAudio?: boolean;
}

interface STTProviderState {
  provider: "deepgram" | "browser" | null;
}

const state: STTProviderState = { provider: null };

async function detectProvider(): Promise<"deepgram" | "browser"> {
  if (state.provider) return state.provider;
  try {
    const res = await fetch("/api/stt/config");
    if (res.ok) {
      const cfg = (await res.json()) as { provider?: string };
      if (cfg.provider === "deepgram" && DeepgramSTT.isSupported()) {
        state.provider = "deepgram";
        return "deepgram";
      }
    }
  } catch { /* fall through */ }
  state.provider = "browser";
  return "browser";
}

class BrowserSTT {
  private recognition: any | null = null;
  private listening = false;

  constructor(private opts: STTOptions) {}

  static isSupported(): boolean {
    return typeof window !== "undefined" &&
      ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);
  }

  start(): void {
    if (this.listening) return;
    const SRClass =
      (window as any).webkitSpeechRecognition ?? (window as any).SpeechRecognition;
    if (!SRClass) {
      this.opts.onError?.("SpeechRecognition not supported in this browser");
      return;
    }
    const rec = new SRClass();
    rec.lang = this.opts.lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event: any) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) this.opts.onResult(final.trim(), true);
      else if (interim) this.opts.onResult(interim.trim(), false);
    };
    rec.onerror = (event: any) => {
      this.listening = false;
      this.opts.onError?.(event.error ?? "unknown");
    };
    rec.onend = () => {
      this.listening = false;
      this.opts.onEnd?.();
    };

    rec.start();
    this.recognition = rec;
    this.listening = true;
  }

  stop(): void {
    if (this.recognition && this.listening) {
      try { this.recognition.stop(); } catch { /* noop */ }
    }
    this.listening = false;
  }

  abort(): void {
    if (this.recognition) {
      try { this.recognition.abort(); } catch { /* noop */ }
    }
    this.listening = false;
  }

  get isListening(): boolean { return this.listening; }
}

/**
 * Unified STT facade. Chooses Deepgram via backend proxy when the server has a
 * key; falls back to webkitSpeechRecognition on Chrome/Edge. On Safari/Firefox
 * without Deepgram, surface a clear error.
 */
export class STT {
  private inner: DeepgramSTT | BrowserSTT | null = null;
  private starting = false;
  private aborted = false;

  constructor(private opts: STTOptions) {}

  static isSupported(): boolean {
    // Deepgram path is always supported (it talks to /api/stt/stream, server
    // decides). If the backend hasn't been configured, we fall through to the
    // browser provider check.
    return DeepgramSTT.isSupported() || BrowserSTT.isSupported();
  }

  start(): void {
    if (this.starting || this.aborted) return;
    this.starting = true;
    void detectProvider().then((provider) => {
      if (this.aborted) {
        this.starting = false;
        return;
      }
      if (provider === "deepgram" && DeepgramSTT.isSupported()) {
        this.inner = new DeepgramSTT({
          lang: this.opts.lang,
          onResult: this.opts.onResult,
          captureAudio: this.opts.captureAudio,
          onError: (err) => {
            // On Deepgram failure, retry once with browser STT if supported.
            if (BrowserSTT.isSupported() && !(this.inner instanceof BrowserSTT)) {
              this.inner = new BrowserSTT(this.opts);
              this.inner.start();
              return;
            }
            this.opts.onError?.(err);
          },
          onEnd: this.opts.onEnd,
        });
      } else if (BrowserSTT.isSupported()) {
        this.inner = new BrowserSTT(this.opts);
      } else {
        this.opts.onError?.("STT not available on this device. Use Chrome on desktop or update the browser.");
        this.starting = false;
        return;
      }
      this.inner.start();
      this.starting = false;
    });
  }

  stop(): void { this.inner?.stop(); }
  abort(): void {
    this.aborted = true;
    this.inner?.abort();
  }
  get isListening(): boolean { return this.inner?.isListening ?? false; }

  // Pronounce-friendly: returns the WebM blob captured during the last
  // utterance (only when captureAudio:true and Deepgram path was used).
  getAudioBlob(): Blob | null {
    if (this.inner && "getAudioBlob" in this.inner) {
      return (this.inner as DeepgramSTT).getAudioBlob();
    }
    return null;
  }
}
